import { useMemo, useState } from "react";
import { classNames } from "../../UI/Utils/classNames.ts";
import { formatHour, weekDaysInOrder } from "../onboardingLiveTime.ts";
import { WeekdayHourEnrollment } from "./onboardingLiveStatistics.ts";
import { formatDecimal } from "./statisticsFormat.ts";
import { HEATMAP_RAMP } from "./statisticsColors.ts";

const WEEKDAY_NAMES = [
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];

type WeekdayHour = { weekdayFromMonday: number; hour: number };

function cellKey({ weekdayFromMonday, hour }: WeekdayHour): string {
  return `${weekdayFromMonday}-${hour}`;
}

function hoursToShow(cells: WeekdayHourEnrollment[]): number[] {
  const hours = cells.map((cell) => cell.hour);
  const first = Math.min(...hours);
  const last = Math.max(...hours);
  return Array.from({ length: last - first + 1 }, (_, index) => first + index);
}

function rampColorOf(intensity: number): string {
  const step = Math.min(
    HEATMAP_RAMP.length - 1,
    Math.floor(intensity * HEATMAP_RAMP.length),
  );
  return HEATMAP_RAMP[step];
}

function describeCell(
  { weekdayFromMonday, hour }: WeekdayHour,
  cell: WeekdayHourEnrollment | undefined,
): string {
  const when = `${WEEKDAY_NAMES[weekdayFromMonday]} ${formatHour(hour)}–${formatHour((hour + 1) % 24)}`;
  if (!cell) return `${when} · nessuna live svolta`;
  return `${when} · ${cell.lives} live · ${formatDecimal(cell.averageEnrolled)} iscritti in media`;
}

export function OnboardingLiveEnrollmentHeatmap({
  cells,
}: {
  cells: WeekdayHourEnrollment[];
}) {
  const [selected, setSelected] = useState<WeekdayHour | null>(null);
  const cellsByKey = useMemo(
    () => new Map(cells.map((cell) => [cellKey(cell), cell])),
    [cells],
  );
  const maxAverageEnrolled = Math.max(
    0,
    ...cells.map((cell) => cell.averageEnrolled),
  );

  if (cells.length <= 0)
    return (
      <p className={"text-sm text-slate-500"}>
        Nessuna live svolta nel periodo.
      </p>
    );

  const hours = hoursToShow(cells);
  const selectedKey = selected ? cellKey(selected) : null;

  return (
    <div className={"flex flex-col gap-3"}>
      <div className={"overflow-x-auto"}>
        <div
          className={"grid gap-0.5"}
          style={{
            gridTemplateColumns: `2.5rem repeat(${hours.length}, minmax(2.25rem, 1fr))`,
          }}
          onMouseLeave={() => setSelected(null)}
        >
          <span />
          {hours.map((hour) => (
            <span
              key={hour}
              className={"text-[10px] text-slate-400 text-center leading-4"}
            >
              {formatHour(hour)}
            </span>
          ))}
          {weekDaysInOrder.map(({ label }, weekdayFromMonday) => (
            <div key={label} className={"contents"}>
              <span className={"text-xs text-slate-500 flex items-center pr-1"}>
                {label}
              </span>
              {hours.map((hour) => {
                const weekdayHour = { weekdayFromMonday, hour };
                const key = cellKey(weekdayHour);
                const cell = cellsByKey.get(key);
                const select = () => setSelected(weekdayHour);
                return (
                  <button
                    key={key}
                    type={"button"}
                    aria-label={describeCell(weekdayHour, cell)}
                    onMouseEnter={select}
                    onFocus={select}
                    onClick={select}
                    className={classNames(
                      "h-8 rounded-sm outline-none text-[10px] font-semibold text-white",
                      cell ? undefined : "bg-white border border-slate-200",
                      selectedKey === key
                        ? "ring-2 ring-slate-700 ring-offset-1"
                        : undefined,
                    )}
                    style={
                      cell
                        ? {
                            backgroundColor: rampColorOf(
                              maxAverageEnrolled > 0
                                ? cell.averageEnrolled / maxAverageEnrolled
                                : 0,
                            ),
                          }
                        : undefined
                    }
                  >
                    {cell ? formatDecimal(cell.averageEnrolled) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <p className={"text-xs text-slate-600 min-h-4"}>
        {selected ? (
          describeCell(selected, cellsByKey.get(cellKey(selected)))
        ) : (
          <span className={"text-slate-400"}>
            Passa sopra una cella per vedere quante live ci sono state e gli
            iscritti medi.
          </span>
        )}
      </p>
      <div
        className={
          "flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500"
        }
      >
        <span className={"flex items-center gap-1.5"}>
          <span
            className={"w-3 h-3 rounded-sm bg-white border border-slate-200"}
          />
          Nessuna live
        </span>
        <span className={"flex items-center gap-1.5"}>
          Iscritti medi per live: meno
          <span className={"flex gap-0.5"}>
            {HEATMAP_RAMP.map((color) => (
              <span
                key={color}
                className={"w-3 h-3 rounded-sm"}
                style={{ backgroundColor: color }}
              />
            ))}
          </span>
          più ({formatDecimal(maxAverageEnrolled)})
        </span>
      </div>
    </div>
  );
}
